import { useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { config } from '../../../config/config';
import { useAppDispatch } from '../../main/store/root.store';
import { selectLocale } from '../../shared/store/locale.store';
import {
    gameplayStore,
    getGameInformation,
    selectSlotCount,
    selectSlotsWithGames,
} from '../store/gameplay.store';

import { useGameplayRefcount } from './use-gameplay-refcount';

export function useGameData({
    initialSlugs,
    onSlugsChange,
}: {
    initialSlugs: Array<string | undefined>;
    onSlugsChange?: (slugs: Array<string | undefined>) => void;
}) {
    useGameplayRefcount();

    const locale = useSelector(selectLocale);
    const slots = useSelector(selectSlotsWithGames);
    const dispatch = useAppDispatch();
    const slotCount = useSelector(selectSlotCount);
    const diffSlugs = useMemo(
        () =>
            initialSlugs.filter((gameSlug): gameSlug is string => {
                return !!gameSlug && !slots.find(({ slug }) => slug === gameSlug);
            }),
        [initialSlugs, slots],
    );

    useEffect(() => {
        // Clamp to maximum 4 slots and ensure if 3 slots are open then
        // we show 4 total available slots.
        const desiredSlotCount = initialSlugs.length >= 3 ? 4 : initialSlugs.length;
        if (slotCount < desiredSlotCount) {
            dispatch(gameplayStore.actions.setSlotCount(desiredSlotCount));
        }

        if (diffSlugs.length) {
            initialSlugs.forEach((slug, index) => {
                dispatch(
                    gameplayStore.actions.setSlotSlug({
                        slug: slug || undefined,
                        slot: index,
                    }),
                );
            });

            dispatch(
                getGameInformation({
                    locale,
                    skinId: config.SKIN_ID,
                    gameSlugs: diffSlugs,
                }),
            );
        }
    }, [diffSlugs, dispatch, initialSlugs, locale, slotCount]);

    // Load any new game slugs game data
    useEffect(() => {
        const newSlugs = slots.filter((slot) => {
            return !slot.isLoadingGameData && !slot.game && !!slot.slug;
        });

        if (newSlugs.length) {
            onSlugsChange?.(slots.map(({ slug }) => slug));

            dispatch(
                getGameInformation({
                    locale,
                    skinId: config.SKIN_ID,
                    gameSlugs: newSlugs.map(({ slug }) => slug) as string[],
                }),
            );
        }
    }, [slots, dispatch, locale, onSlugsChange]);

    const updateSlotCount = useCallback(
        (count: number) => {
            dispatch(gameplayStore.actions.setSlotCount(count));

            if (count < slotCount) {
                onSlugsChange?.(
                    Array.from({ length: count })
                        .fill(null)
                        .map((_, index) => {
                            return slots[index].slug;
                        }),
                );
            }
        },
        [dispatch, slotCount, slots, onSlugsChange],
    );

    return {
        slots,
        updateSlotCount,
    };
}
